// Internal modules
import { appStore } from '@/store';
import { logger } from '../logger';
import { features } from '../features';
import { getAccessToken, getLocale, getToken } from '../globals';
import { validOneTouchCountries } from './constants';

/**
 * @function dropLoginCookie
 * @returns {Promise}
 * @description drops a login cookie and resolves the promise with
 * `true` if it was a success or `false` if it wasn't
 */
interface ObjectWithFlags {
  flags: {
    [x: string]: any;
  };
}

// Type Guard to ensure value has a flags property
const hasFlags = (value: unknown): value is ObjectWithFlags => {
  return (value as ObjectWithFlags)?.flags !== undefined;
};

const dropLoginCookie = (data: unknown) => {
  const { type: tokenType, value: tokenValue } = getAccessToken();
  const flags = hasFlags(data) ? data.flags : {};

  if (!tokenValue || (tokenType !== 'signup' && !flags?.is3DSecureRequired)) {
    logger.warn('abort_auth_cookie_drop', {
      tokenPresent: Boolean(tokenValue),
      tokenType,
    });
    return Promise.resolve(false);
  }

  const options: RequestInit = {
    credentials: 'same-origin',
    headers: {
      'x-paypal-internal-euat': tokenValue,
    },
    method: 'GET',
  };

  return fetch('/checkoutweb/drop', options)
    .then(() => {
      logger.info('auth_cookie_dropped_successfully');
      return true;
    })
    .catch(err => {
      logger.error('auth_cookie_drop_failed', {
        error: err.message,
      });
      return false;
    });
};

const conditionallyDropLoginCookie = (
  valueToReturn?: unknown,
): Promise<unknown | undefined> => {
  const { user: userActions } = appStore.getActions();
  const {
    ui: uiState,
    user: { isUserLoggedIn },
  } = appStore.getState();
  const { country } = getLocale();
  const isSignUp = getAccessToken().type === 'signup';
  // TODO: refactor flags to be a param / seperate from "valueToReturn"
  if (hasFlags(valueToReturn)) {
    const flags = valueToReturn.flags;
    if ((!flags?.is3DSecureRequired && !isSignUp) || isUserLoggedIn) {
      return Promise.resolve(valueToReturn);
    }
  }

  // Opt into one touch if POMA flow
  const isPOMAEligible = features.isActive('isPOMAEligible');
  const isPasswordlessForcedSignup = features.isActive(
    'passwordlessForcedSignup',
  );
  const isStreamlinePasswordlessForcedSignup =
    features.isActive('isStreamlineForcedExperience') &&
    isPasswordlessForcedSignup;

  const isPasswordlessOptionalSignup = features.isActive(
    'passwordlessOptionalSignup',
  );
  const isStreamlinePasswordlessOptionalSignup =
    features.isActive('isStreamlineOptionalExperience') &&
    isPasswordlessOptionalSignup;

  const isPasswordlessUpgrade = features.isActive('isPasswordlessUpgrade');

  const webLLSTreatment = features.isActive('isWebLLS');

  const isPayLater = uiState.fiType === 'bnpl';
  const isPOMAFlow =
    (isPOMAEligible ||
      isPasswordlessForcedSignup ||
      isPasswordlessUpgrade ||
      isStreamlinePasswordlessForcedSignup ||
      isStreamlinePasswordlessOptionalSignup) &&
    !isPayLater;
  const oneTouchAllowed = validOneTouchCountries.includes(country);

  const shouldDropOneTouchCookie =
    isPOMAFlow && oneTouchAllowed && !webLLSTreatment;

  if (shouldDropOneTouchCookie) {
    logger.info('setting_poma_one_touch_cookie');

    const token = getToken();

    // Lazy loading function since it's only used in the POMA flow
    return (
      import('@/components/PasswordlessOnboarding/utils')
        .then(({ dropOneTouchCookie }) => dropOneTouchCookie(token))
        .then(() => userActions.setUserLoggedIn())
        .catch(error => {
          logger.error('drop_one_touch_cookie_error', {
            message: error?.message || error,
          });
        })
        // Continue with flow even if one touch activation fails
        .then(() => valueToReturn)
    );
  }

  // Drop login cookie for non-POMA signup
  return dropLoginCookie(valueToReturn).then(isUserLoggedIn => {
    isUserLoggedIn && userActions.setUserLoggedIn();
    return valueToReturn;
  });
};

export { conditionallyDropLoginCookie };
